<template>
  <div class="kpi-measure-editor">
    <div class="editor-header">
      <div class="widget-type">
        <img
          :src="moduleConfigs.icon"
          alt="" />
        <div>{{ moduleConfigs.name }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field"
            message="Names the card, and fills the measure chip in every row label.">
            <b-input v-model="inputs.name" />
          </b-field>
        </div>
      </div>

      <div
        id="measure"
        class="editor-section">
        <h2>Measure</h2>
        <div class="body">
          <MeasureConfigEditor
            v-model="inputs.measure_config"
            :errors="childErrors('measure_config', errors)"
            :context="{
              reportDimensions: [],
              tableDefaultTimeRange: context.moduleDefaultTimeRange,
              timeRangeDefaultLabel: context.timeRangeDefaultLabel,
              timeRangeDefaultName: context.timeRangeDefaultName,
            }" />
        </div>
      </div>

      <div
        id="direction"
        class="editor-section">
        <h2>Direction</h2>
        <div class="body">
          <b-field
            label="Which way is good for this measure?"
            class="input-field">
            <singleselect
              v-model="goodDirection"
              :append-to-body="true"
              :options="goodDirectionOptions"
              :return-as-object="false"
              :enable-search="false"
              label="label"
              track-by="key"
              header-placeholder="Select direction" />
          </b-field>
          <p class="section-help">
            Every row of this KPI is coloured in this direction. Neutral never
            colours, whatever the rows say. A row that moves the opposite way
            can invert it.
          </p>

          <b-field
            label="A change in this measure reads as:"
            class="input-field mt-5">
            <singleselect
              v-model="differenceType"
              :append-to-body="true"
              :options="differenceTypeOptions"
              :return-as-object="false"
              :enable-search="false"
              label="label"
              track-by="key"
              header-placeholder="Select difference type" />
          </b-field>
          <p class="section-help">
            Used by rows set to show the measure's own difference, so one row
            can read as a percentage for some measures and a money difference
            for others.
          </p>
        </div>
      </div>

      <div
        id="style"
        class="editor-section">
        <h2>Style</h2>
        <div class="body">
          <b-field class="input-field">
            <b-switch
              v-model="isHero"
              type="is-info">
              Show as the headline figure
            </b-switch>
          </b-field>
          <p class="section-help">
            The headline is set once per module and rendered large above the
            other measures. Turning it on here turns it off wherever it was
            before. With none set, the module's first measure is used.
          </p>
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :measure="inputs.measure_config?.measure"
            :type-override="formatTypeOverride" />
          <p class="section-help">
            Rows without a format of their own use this one.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import singleselect from '@/components/ui/singleselect.vue'
  import EditorSectionsMixin from '@/components/pdf_report/EditorSectionsMixin'
  import MeasureConfigEditor from '@/components/reports/config/modules/common/MeasureConfigEditor'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'KpiMeasure',
    components: {
      Tabs,
      singleselect,
      MeasureConfigEditor,
      DataFormatEditor,
    },
    mixins: [EditorSectionsMixin,],
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      context: {
        type: Object,
        default: () => {},
      },
    },
    data () {
      return {
        moduleConfigs: {
          name: 'KPI Measure',
          type: 'KpiMeasure',
          icon: require('@/assets/icons/kpi-measure.svg'),
        },
        inputSectionTab: 'display-label',

        goodDirectionOptions: [
          { key: null, label: 'Not set — each row decides', },
          { key: 'higher_is_better', label: 'Higher is better', },
          { key: 'lower_is_better', label: 'Lower is better', },
          { key: 'neutral', label: 'Neutral — never colour', },
        ],

        differenceTypeOptions: [
          { key: null, label: 'Not set — rows default to a difference', },
          { key: 'diff', label: 'Difference to benchmark', },
          { key: 'var', label: 'Percentage change from benchmark', },
        ],
      }
    },
    computed: {
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },

      isHero: {
        get () {
          return !!this.inputs.is_hero
        },
        set (value) {
          this.$set(this.inputs, 'is_hero', value)
          // One headline per module. Cleared on the siblings here rather than
          // left to the renderer, which takes the first marked -- two marks
          // would put the star on one measure and the headline on another.
          if (value) {
            this.$emit('exclusive-hero')
          }
        },
      },
      // `undefined` on a measure saved before these settings existed and
      // `null` once cleared both mean "not set"; normalise so the select
      // lands on the right row either way.
      goodDirection: {
        get () {
          return this.inputs.good_direction ?? null
        },
        set (value) {
          this.$set(this.inputs, 'good_direction', value)
        },
      },
      differenceType: {
        get () {
          return this.inputs.difference_type ?? null
        },
        set (value) {
          this.$set(this.inputs, 'difference_type', value)
        },
      },
      formatTypeOverride () {
        if (this.inputs.measure_config?.is_pct_total) return 'percentage'
        return null
      },
      tabs () {
        return [
          {
            label: 'Display label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Measure',
            id: 'measure',
            hasErrors: _childErrors('measure_config', this.errors).length > 0,
          },
          {
            label: 'Direction',
            id: 'direction',
            hasErrors: false,
          },
          {
            label: 'Style',
            id: 'style',
            hasErrors: false,
          },
          {
            label: 'Format',
            id: 'format',
            hasErrors: _childErrors('data_format', this.errors).length > 0,
          },
        ]
      },
    },
    methods: {
      childErrors: _childErrors,
    },
  }
</script>

<style lang="scss" scoped>
@import '@/components/pdf_report/kpi_module/kpi-editor-panel.scss';
</style>
